'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { gsap } from 'gsap';
import { useEffects } from '@/contexts/EffectsContext';
import { prefersReducedMotion } from '@/lib/effects';

const links = [
  { name: 'Home', path: '/homepage' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Portfolio', path: '/portfolio' },
  { name: 'Contact', path: '/contact' },
];

export default function OverlayMenu() {
  const { isMenuOpen, setMenuOpen } = useEffects();
  const pathname = usePathname();
  const navRef = useRef<HTMLElement>(null);
  const itemsRef = useRef<HTMLLIElement[]>([]);
  const bgRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const nav = navRef.current;
    const items = itemsRef.current.filter(Boolean);
    const bg = bgRef.current;
    const footer = footerRef.current;
    if (!nav || !items.length || !bg) return;
    const reduced = prefersReducedMotion();

    if (isMenuOpen) {
      nav.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      
      if (!reduced) {
        // Background fade in
        gsap.fromTo(bg, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' });
        
        // Items slide up with stagger
        gsap.fromTo(items, 
          { y: 80, opacity: 0, scale: 0.9 }, 
          { 
            y: 0, 
            opacity: 1, 
            scale: 1, 
            duration: 0.7, 
            stagger: 0.08, 
            ease: 'power3.out', 
            delay: 0.2 
          }
        );

        // Footer fade in
        if (footer) {
          gsap.fromTo(footer, 
            { opacity: 0, y: 20 }, 
            { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', delay: 0.8 }
          );
        }
      } else {
        gsap.set(bg, { opacity: 1 });
        gsap.set(items, { y: 0, opacity: 1, scale: 1 });
        if (footer) gsap.set(footer, { opacity: 1, y: 0 });
      }
    } else {
      document.body.style.overflow = '';
      
      if (!reduced) {
        gsap.to(items, {
          y: -40, 
          opacity: 0, 
          scale: 0.95, 
          duration: 0.3, 
          stagger: 0.04, 
          ease: 'power2.in',
        });
        if (footer) {
          gsap.to(footer, { opacity: 0, y: 10, duration: 0.2 });
        }
        gsap.to(bg, {
          opacity: 0,
          duration: 0.3,
          onComplete: () => nav.classList.remove('is-open'),
        });
      } else {
        nav.classList.remove('is-open');
      }
    }
  }, [isMenuOpen]);

  return (
    <nav data-component-id="src/components/effects/OverlayMenu.tsx:95:4" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="95" data-component-end-line="149" data-component-file="OverlayMenu.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22overlay-nav%20fixed%20inset-0%20z-%5B998%5D%20flex%20items-center%20justify-center%22%7D" 
      ref={navRef} 
      className="overlay-nav fixed inset-0 z-[998] flex items-center justify-center" 
      aria-hidden={!isMenuOpen}
    >
      {/* Background with gradient */}
      <div data-component-id="src/components/effects/OverlayMenu.tsx:101:6" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="101" data-component-end-line="105" data-component-file="OverlayMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20inset-0%20bg-gradient-to-br%20from-background%20via-background%20to-card%2F50%20backdrop-blur-2xl%22%7D" 
        ref={bgRef}
        className="absolute inset-0 bg-gradient-to-br from-background via-background to-card/50 backdrop-blur-2xl"
        style={{ opacity: 0 }}
      />
      
      {/* Decorative elements */}
      <div data-component-id="src/components/effects/OverlayMenu.tsx:108:6" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="108" data-component-end-line="108" data-component-file="OverlayMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-1%2F4%20left-1%2F4%20w-64%20h-64%20bg-primary%2F5%20rounded-full%20blur-3xl%22%7D" className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
      <div data-component-id="src/components/effects/OverlayMenu.tsx:109:6" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="109" data-component-end-line="109" data-component-file="OverlayMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-1%2F4%20right-1%2F4%20w-96%20h-96%20bg-primary%2F3%20rounded-full%20blur-3xl%22%7D" className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/3 rounded-full blur-3xl" />

      {/* Menu items */}
      <ul data-component-id="src/components/effects/OverlayMenu.tsx:112:6" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="112" data-component-end-line="142" data-component-file="OverlayMenu.tsx" data-component-name="ul" data-component-content="%7B%22elementName%22%3A%22ul%22%2C%22className%22%3A%22list-none%20p-0%20m-0%20text-center%20space-y-1%20relative%20z-10%22%7D" className="list-none p-0 m-0 text-center space-y-1 relative z-10">
        {links.map((item, i) => {
          const isActive = pathname === item.path || (item.path === '/homepage' && pathname === '/');
          return (
            <li data-component-id="src/components/effects/OverlayMenu.tsx:116:12" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="116" data-component-end-line="139" data-component-file="OverlayMenu.tsx" data-component-name="li" data-component-content="%7B%22elementName%22%3A%22li%22%2C%22className%22%3A%22opacity-0%22%7D" 
              key={item.path} 
              ref={(el) => { if (el) itemsRef.current[i] = el; }} 
              className="opacity-0"
            >
              <Link data-component-id="src/components/effects/OverlayMenu.tsx:121:14" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="121" data-component-end-line="138" data-component-file="OverlayMenu.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22className%22%3A%22nav-overlay-link%20relative%20block%20py-3%20px-8%20group%22%7D"
                href={item.path}
                onClick={() => setMenuOpen(false)}
                className="nav-overlay-link relative block py-3 px-8 group"
                data-cursor
              >
                <span data-component-id="src/components/effects/OverlayMenu.tsx:127:16" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="127" data-component-end-line="131" data-component-file="OverlayMenu.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`relative z-10 text-[clamp(2.5rem,7vw,5rem)] font-outfit font-bold uppercase tracking-tight transition-colors duration-300 ${
                  isActive ? 'text-primary' : 'text-foreground group-hover:text-primary'
                }`}>
                  {item.name}
                </span>
                {/* Active indicator */}
                {isActive && (
                  <span data-component-id="src/components/effects/OverlayMenu.tsx:134:18" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="134" data-component-end-line="134" data-component-file="OverlayMenu.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20left-0%20top-1%2F2%20-translate-y-1%2F2%20w-2%20h-2%20bg-primary%20rounded-full%22%7D" className="absolute left-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-primary rounded-full" />
                )}
                {/* Hover line */}
                <span data-component-id="src/components/effects/OverlayMenu.tsx:137:16" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="137" data-component-end-line="137" data-component-file="OverlayMenu.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22absolute%20bottom-0%20left-1%2F2%20-translate-x-1%2F2%20w-0%20h-%5B3px%5D%20bg-primary%20group-hover%3Aw-full%20transition-all%20duration-500%20ease-out-expo%22%7D" className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-[3px] bg-primary group-hover:w-full transition-all duration-500 ease-out-expo" />
              </Link>
            </li>
          );
        })}
      </ul>

      {/* Footer info */}
      <div data-component-id="src/components/effects/OverlayMenu.tsx:145:6" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="145" data-component-end-line="148" data-component-file="OverlayMenu.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20bottom-12%20left-1%2F2%20-translate-x-1%2F2%20text-center%20space-y-2%22%7D" ref={footerRef} className="absolute bottom-12 left-1/2 -translate-x-1/2 text-center space-y-2" style={{ opacity: 0 }}>
        <p data-component-id="src/components/effects/OverlayMenu.tsx:146:8" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="146" data-component-end-line="146" data-component-file="OverlayMenu.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20font-inter%20text-sm%22%2C%22textContent%22%3A%22hub%40dsmburo.com%22%7D" className="text-text-secondary font-inter text-sm">hub@dsmburo.com</p>
        <p data-component-id="src/components/effects/OverlayMenu.tsx:147:8" data-component-path="src/components/effects/OverlayMenu.tsx" data-component-line="147" data-component-end-line="147" data-component-file="OverlayMenu.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%20font-inter%20text-sm%22%2C%22textContent%22%3A%22%2B971%2056%20371%203696%22%7D" className="text-text-secondary font-inter text-sm">+971 56 371 3696</p>
      </div>
    </nav>
  );
}
